/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Edit Claim Request
 */
import { Write } from '@icon-park/react';
import { Button as AntButton, Tooltip, ButtonProps, App } from 'antd';

import { getPayerMeta } from 'utils/eligibility/eligibilityResponse';
import { Link } from 'react-router-dom';
import { getEditClaimRequestPath } from 'utils/router';
import { MODAL_TYPES } from 'components/widgets/modal';

const getModalContent = (isArchivedPayer: boolean, isInValidNpi: boolean, isValidLocation: boolean): string => {
	if (isArchivedPayer && isInValidNpi) {
		return "Can't edit this request anymore as this payer is archived and the NPI is disabled or has not completed eKYC verification yet.";
	}
	if (isArchivedPayer) {
		return "Can't edit this request anymore as this payer is archived.";
	}
	if (!isValidLocation) {
		return "Can't edit this request anymore as this location is disabled.";
	}
	return "Can't edit this request anymore as this NPI is disabled or has not completed eKYC verification yet.";
};

const commonProps: ButtonProps = { color: 'default' };

const EditClaimRequest: React.FC<
	{
		id: string;
		payer?: Payers.ISelectedPayer;
		providerMeta?: Metadata.IProviderMeta;
		isValidLocation: boolean;
		label?: React.ReactNode;
	} & ButtonProps
> = ({ id, payer, providerMeta, isValidLocation, label, ...restProps }) => {
	const { modal } = App.useApp();
	const combinedProps = { ...commonProps, ...restProps };
	const isArchivedPayer = getPayerMeta(payer).isArchived;
	const isInValidNpi = !providerMeta?.canMakeRequests;

	const handleClick = () => {
		modal.error({
			...MODAL_TYPES.warning,
			width: 450,
			content: getModalContent(isArchivedPayer, isInValidNpi, isValidLocation),
		});
	};

	if (!id) {
		return null;
	}

	const isInvalid = isArchivedPayer || isInValidNpi || !isValidLocation;

	const button = label ? (
		<AntButton {...combinedProps} onClick={isInvalid ? handleClick : undefined}>
			{label}
		</AntButton>
	) : (
		<Tooltip title='Edit Request'>
			<AntButton {...combinedProps} onClick={isInvalid ? handleClick : undefined} icon={<Write />} />
		</Tooltip>
	);

	if (isInvalid) {
		return button;
	}

	return (
		<Link {...restProps} to={getEditClaimRequestPath(id)}>
			{button}
		</Link>
	);
};

export default EditClaimRequest;
